import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import { Input } from '@roger/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Textarea } from '@roger/ui-components';
import { useToast } from '../hooks/useToastContext';
import { getPayors } from '../services/api';
import { API_BASE_URL } from '../services/api';
import type { PayorResponse } from '../services/api/types.gen';
import { PayorCombobox } from './PayorCombobox';
import { NpiInput } from './NpiInput';

// Interface definitions matching the backend Claim837I model

interface Submitter {
  type: 'Organization' | 'Individual';
  name: string;
  sbmtter_contact_name?: string;
  sbmtter_contacts: [string, string][];
  identifier?: string;
  identifier_type?: string;
}

interface Receiver {
  type: 'Organization' | 'Individual';
  name: string;
  identifier?: string;
  identifier_type?: string;
}

interface Subscriber {
  subscriber_identifier: string;
  name: string;
  patient_relationship_cd?: string;
  subscriber_relationship_cd: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  dob: string;
  dob_format: string;
  gender_cd: 'M' | 'F';
  mrn: string;
  group_number?: string;
  group_name?: string;
  insurance_type_code?: string;
  coordination_of_benefits_code?: string;
  yes_no_condition?: string;
  employment_status_code?: string;
  claim_filing_indicator_code?: string;
}

interface Patient {
  subscriber_identifier: string;
  name: string;
  patient_relationship_cd?: string;
  subscriber_relationship_cd: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  dob: string;
  dob_format: string;
  gender_cd: 'M' | 'F';
  mrn: string;
}

interface Payer {
  payer_name: string;
  payer_identifier: string;
  payer_identifier_cd: string;
  business_entity_type: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface BillingProvider {
  npi?: string;
  entity_type: 'Organization' | 'Individual';
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  ein_type?: string;
  ein?: string;
  taxonomy?: string;
  provider_role?: string;
}

interface AttendingOperatingProvider {
  npi?: string;
  entity_type: 'Organization' | 'Individual';
  name?: string;
  ein_type?: string;
  ein?: string;
  taxonomy?: string;
  provider_role?: string;
}

interface ServiceFacilityProvider {
  npi?: string;
  entity_type: 'Organization' | 'Individual';
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface Providers {
  billing: BillingProvider;
  attending: AttendingOperatingProvider;
  operating: AttendingOperatingProvider;
  service_facility: ServiceFacilityProvider;
}

interface ClaimDate {
  date_cd: string;
  date_format: string;
  date: string;
}

interface ClaimRef {
  id_code_qualifier: string;
  id: string;
}

interface ValueCode {
  code_qualifier: string;
  code: string;
  amount?: string;
  quantity?: string;
}

interface OtherAmount {
  qualifier: string;
  amount: string;
}

interface ClaimHeader {
  claim_id: string;
  claim_amount: string;
  facility_type_code: string;
  claim_dates: ClaimDate[];
  admission_type: string;
  admission_src_cd: string;
  discharge_status_cd: string;
  encounter_id?: string;
  drg_cd?: string;
  clm_refs: ClaimRef[];
  other_amts: OtherAmount[];
  value_codes?: ValueCode[];
  frequency_code?: string;
  provider_accept_assignment?: string;
  assignment_of_benefits?: string;
}

interface ServiceDate {
  date_cd: string;
  date_format: string;
  date: string;
}

interface ClaimLine {
  claim_line_number: string;
  service_dates: ServiceDate[];
  units: string;
  units_measurement: string;
  line_chrg_amt: string;
  prcdr_cd: string;
  prcdr_cd_type: string;
  modifier_cds?: string;
  revenue_cd: string;
}

interface DiagnosisCode {
  dx_cd: string;
  poa: string;
}

interface Diagnosis {
  principal_dx_cd: string;
  principal_dx_poa: string;
  admitting_dx_cd: string;
  admitting_dx_poa: string;
  reason_visit_dx_cd?: string;
  reason_visit_dx_poa?: string;
  external_injury_dx_cd?: string;
  external_injury_dx_poa?: string;
  other_dx_cds: DiagnosisCode[];
}

interface Claim837I {
  submitter: Submitter;
  receiver: Receiver;
  subscriber: Subscriber;
  patient: Patient;
  payer: Payer;
  providers: Providers;
  claim_header: ClaimHeader;
  claim_lines: ClaimLine[];
  diagnosis: Diagnosis;
}

interface X12Response {
  x12_message: string;
  transaction_type: string;
  control_number: string;
}

interface ClaimSubmissionResponse {
  status: string;
  claim_id: string;
  control_number: string;
  remote_filename: string;
  submission_timestamp: string;
  message: string;
}

interface X12Error {
  segment_id: string;
  segment_position: string;
  loop_id: string;
  error_code: string;
  error_description: string;
  context?: string;
}

interface FunctionalGroupAck {
  status: string;
  accepted_count: number;
  received_count: number;
}

interface TransactionSetAck {
  transaction_set_control_number: string;
  status: string;
  error_codes?: string[];
}

interface AckParsed {
  status: string;
  status_description?: string;
  sender_id?: string;
  receiver_id?: string;
  control_number?: string;
  transaction_control_number?: string;
  interchange_control_number?: string;
  statistics?: {
    total_segments: number;
    accepted_segments: number;
    rejected_segments: number;
    error_count: number;
  };
  functional_group_acks?: FunctionalGroupAck[];
  transaction_set_acks?: TransactionSetAck[];
  errors?: X12Error[];
}

interface AcknowledgmentResponse {
  status: 'found' | 'processing' | 'not_found';
  claim_id: string;
  control_number?: string;
  ack_filename?: string;
  ack_content?: string;
  ack_parsed?: AckParsed;
  message: string;
}

interface ValidationError {
  loc: string[];
  msg: string;
  type?: string;
}

// Common revenue codes for institutional claims
const revenueCodeOptions = [
  { value: '0124', label: '0124 - Medical/Surgical/GYN 2 bed' },
  { value: '0250', label: '0250 - Pharmacy - General' },
  { value: '0260', label: '0260 - IV Therapy' },
  { value: '0300', label: '0300 - Laboratory - General' },
  { value: '0301', label: '0301 - Laboratory - Chemistry' },
  { value: '0305', label: '0305 - Laboratory - Hematology' },
  { value: '0306', label: '0306 - Laboratory - Bacteriology' },
  { value: '0307', label: '0307 - Laboratory - Urology' },
  { value: '0351', label: '0351 - CT Scan - Head' },
  { value: '0320', label: '0320 - Radiology - Diagnostic' },
  { value: '0360', label: '0360 - Operating Room Services' },
  { value: '0370', label: '0370 - Anesthesia' },
  { value: '0410', label: '0410 - Respiratory Services' },
  { value: '0730', label: '0730 - EKG/ECG' },
];

// Facility codes
const facilityCodeOptions = [
  { value: '11:A:1', label: '11:A:1 - Hospital' },
  { value: '12', label: '12 - Hospital Part B' },
  { value: '13', label: '13 - Hospital Swing Bed' },
  { value: '21', label: '21 - Skilled Nursing - Inpatient' },
  { value: '22', label: '22 - Skilled Nursing - Outpatient' },
  { value: '31', label: '31 - Skilled Nursing - Patient Part A' },
  { value: '32', label: '32 - Nursing Facility - Inpatient Part B' },
  { value: '61', label: '61 - Comprehensive Inpatient Rehab' },
];

// Admission type codes
const admissionTypeOptions = [
  { value: '1', label: '1 - Emergency' },
  { value: '2', label: '2 - Urgent' },
  { value: '3', label: '3 - Elective' },
  { value: '4', label: '4 - Newborn' },
  { value: '5', label: '5 - Trauma Center' },
];

// Patient status codes
const patientStatusOptions = [
  { value: '01', label: '01 - Discharged to home' },
  { value: '02', label: '02 - Discharged/transferred to short-term hospital' },
  { value: '03', label: '03 - Discharged/transferred to SNF' },
  { value: '04', label: '04 - Discharged/transferred to ICF' },
  {
    value: '05',
    label: '05 - Discharged/transferred to another type of facility',
  },
  { value: '06', label: '06 - Discharged/transferred to home health' },
  { value: '07', label: '07 - Left against medical advice' },
  { value: '20', label: '20 - Expired' },
  { value: '30', label: '30 - Still patient' },
];

const X12_837I_Testing: React.FC = () => {
  const { success, error } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [x12Response, setX12Response] = useState<X12Response | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [payors, setPayors] = useState<PayorResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResponse, setSubmissionResponse] =
    useState<ClaimSubmissionResponse | null>(null);
  const [isCheckingAck, setIsCheckingAck] = useState(false);
  const [ackResponse, setAckResponse] = useState<AcknowledgmentResponse | null>(
    null
  );

  // Helper function to convert status codes to full text
  const getStatusText = (statusCode: string): string => {
    const upperCode = statusCode?.toUpperCase();
    switch (upperCode) {
      case 'A':
      case 'ACCEPTED':
        return 'Accepted';
      case 'R':
      case 'REJECTED':
        return 'Rejected';
      case 'P':
      case 'PARTIALLY ACCEPTED':
        return 'Partially Accepted';
      default:
        return statusCode || '';
    }
  };

  // Helper function to get status color classes
  const getStatusColor = (statusCode: string): string => {
    const upperCode = statusCode?.toUpperCase();
    switch (upperCode) {
      case 'A':
      case 'ACCEPTED':
        return 'text-green-700';
      case 'R':
      case 'REJECTED':
        return 'text-red-700';
      case 'P':
      case 'PARTIALLY ACCEPTED':
        return 'text-yellow-700';
      default:
        return 'text-gray-700';
    }
  };

  // Helper function to format error messages
  const formatErrorMessage = (error: unknown): string => {
    // If error is already a string, return it
    if (typeof error === 'string') {
      return error;
    }

    // If error is an Error instance, use its message
    if (error instanceof Error) {
      return error.message;
    }

    // If error is an object with a message property, use it
    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
    ) {
      return error.message;
    }

    // Try to stringify the error
    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return 'An unknown error occurred';
    }
  };

  const [formData, setFormData] = useState<Claim837I>({
    submitter: {
      type: 'Organization',
      name: 'TALEND',
      sbmtter_contact_name: 'SAMBIT',
      sbmtter_contacts: [
        ['TE', '3143202264'],
        ['EX', '8099264'],
      ],
      identifier: 'ROGERMR',
      identifier_type: '46',
    },
    receiver: {
      type: 'Organization',
      name: 'WAYSTAR',
      identifier: 'WAYSTAR',
      identifier_type: '46',
    },
    subscriber: {
      subscriber_identifier: 'U0000000001',
      name: 'TEST MEMBER',
      patient_relationship_cd: undefined,
      subscriber_relationship_cd: '18',
      street: '123 MAIN STREET',
      city: 'Elgin',
      state: 'IL',
      zip: '601200001',
      dob: '19960215',
      dob_format: 'D8',
      gender_cd: 'F',
      mrn: 'MRN12345',
      group_number: 'GRP12345',
      group_name: 'ACME CORPORATION',
      insurance_type_code: '12',
      coordination_of_benefits_code: '1',
      yes_no_condition: 'Y',
      employment_status_code: '1',
      claim_filing_indicator_code: 'MC',
    },
    patient: {
      subscriber_identifier: 'U0000000001',
      name: 'TEST MEMBER',
      patient_relationship_cd: undefined,
      subscriber_relationship_cd: '18',
      street: '123 MAIN STREET',
      city: 'Elgin',
      state: 'IL',
      zip: '601200001',
      dob: '19960215',
      dob_format: 'D8',
      gender_cd: 'F',
      mrn: 'MRN12345',
    },
    payer: {
      payer_name: 'CENTENE',
      payer_identifier: 'WAYSTAR',
      payer_identifier_cd: 'PI',
      business_entity_type: '2',
      street: '123 MAIN STREET',
      city: 'CHICAGO',
      state: 'IL',
      zip: '606010000',
    },
    providers: {
      billing: {
        npi: '1234567890',
        entity_type: 'Organization',
        name: 'Test Provider',
        street: '123 MAIN STREET',
        city: 'WOODSTOCK',
        state: 'IL',
        zip: '600980000',
        ein_type: 'EI',
        ein: '123456789',
        taxonomy: '273R00000X',
        provider_role: 'BI',
      },
      attending: {
        npi: '9876543210',
        entity_type: 'Individual',
        name: 'DR SARAH WILLIAMS',
        ein_type: undefined,
        ein: undefined,
        taxonomy: '207R00000X',
        provider_role: 'AT',
      },
      operating: {
        npi: '5432167890',
        entity_type: 'Individual',
        name: 'DR ROBERT CHEN',
        ein_type: 'EI',
        ein: '987654321',
        taxonomy: '208600000X',
        provider_role: 'OP',
      },
      service_facility: {
        npi: '1112223334',
        entity_type: 'Organization',
        name: 'BETHANY HOMES AND METHODIST HOSPITAL',
        street: '240 WILSON RD',
        city: 'CHICAGO',
        state: 'IL',
        zip: '606400000',
      },
    },
    claim_header: {
      claim_id: 'TD-R192ICE00087',
      claim_amount: '17166.7',
      facility_type_code: '11:A:1',
      claim_dates: [
        { date_cd: '096', date_format: 'TM', date: '0900' },
        { date_cd: '434', date_format: 'RD8', date: '20180628-20180702' },
        { date_cd: '435', date_format: 'DT', date: '201806280800' },
      ],
      admission_type: '1',
      admission_src_cd: '1',
      discharge_status_cd: '01',
      encounter_id: 'ENC-2024-789456',
      drg_cd: 'DR:885',
      clm_refs: [{ id_code_qualifier: 'F8', id: 'TD-R192ICE00087' }],
      other_amts: [],
      value_codes: [
        { code_qualifier: 'BE', code: '01', amount: '1563', quantity: '' },
        { code_qualifier: 'BE', code: '80', amount: '4', quantity: '' },
      ],
      frequency_code: '1',
      provider_accept_assignment: 'Y',
      assignment_of_benefits: 'Y',
    },
    claim_lines: [
      {
        claim_line_number: '1',
        service_dates: [
          { date_cd: '472', date_format: 'RD8', date: '20180628-20180702' },
        ],
        units: '4',
        units_measurement: 'UN',
        line_chrg_amt: '9148',
        prcdr_cd: '99233',
        prcdr_cd_type: 'HC',
        modifier_cds: undefined,
        revenue_cd: '0124',
      },
    ],
    diagnosis: {
      principal_dx_cd: 'F39',
      principal_dx_poa: 'Y',
      admitting_dx_cd: 'F39',
      admitting_dx_poa: 'Y',
      reason_visit_dx_cd: undefined,
      reason_visit_dx_poa: undefined,
      external_injury_dx_cd: undefined,
      external_injury_dx_poa: undefined,
      other_dx_cds: [
        { dx_cd: 'F1319', poa: 'Y' },
        { dx_cd: 'F419', poa: 'Y' },
        { dx_cd: 'F17210', poa: 'Y' },
        { dx_cd: 'E876', poa: 'N' },
      ],
    },
  });

  // Fetch payors on component mount
  useEffect(() => {
    const fetchPayors = async () => {
      try {
        const payorsData = await getPayors({});
        if (payorsData && payorsData.data) {
          setPayors(payorsData.data);
        }
      } catch (err) {
        console.error('Failed to fetch payors:', err);
        error('Failed to load payor list');
      }
    };

    fetchPayors();
  }, [error]);

  const handleSubmitterChange = <K extends keyof Submitter>(
    field: K,
    value: Submitter[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      submitter: {
        ...prev.submitter,
        [field]: value,
      },
    }));
  };

  const handleReceiverChange = <K extends keyof Receiver>(
    field: K,
    value: Receiver[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      receiver: {
        ...prev.receiver,
        [field]: value,
      },
    }));
  };

  const handleSubscriberChange = <K extends keyof Subscriber>(
    field: K,
    value: Subscriber[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      subscriber: {
        ...prev.subscriber,
        [field]: value,
      },
    }));
  };

  const handlePayerChange = <K extends keyof Payer>(
    field: K,
    value: Payer[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      payer: {
        ...prev.payer,
        [field]: value,
      },
    }));
  };

  const handleBillingProviderChange = <K extends keyof BillingProvider>(
    field: K,
    value: BillingProvider[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        billing: {
          ...prev.providers.billing,
          [field]: value,
        },
      },
    }));
  };

  const handleServiceFacilityChange = <K extends keyof ServiceFacilityProvider>(
    field: K,
    value: ServiceFacilityProvider[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        service_facility: {
          ...prev.providers.service_facility,
          [field]: value,
        },
      },
    }));
  };

  const handleClaimHeaderChange = <K extends keyof ClaimHeader>(
    field: K,
    value: ClaimHeader[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      claim_header: {
        ...prev.claim_header,
        [field]: value,
      },
    }));
  };

  const handleDiagnosisChange = <K extends keyof Diagnosis>(
    field: K,
    value: Diagnosis[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      diagnosis: {
        ...prev.diagnosis,
        [field]: value,
      },
    }));
  };

  const handlePayorSelect = (payorId: string | null) => {
    if (!payorId) {
      // Clear the payor fields
      setFormData(prev => ({
        ...prev,
        payer: {
          ...prev.payer,
          payer_identifier: '',
          payer_name: '',
        },
      }));
      return;
    }

    const selectedPayor = payors.find(p => p.id === payorId);
    if (selectedPayor) {
      setFormData(prev => ({
        ...prev,
        payer: {
          ...prev.payer,
          payer_identifier:
            selectedPayor.electronic_payor_id ||
            selectedPayor.clearinghouse_payor_id ||
            selectedPayor.id,
          payer_name: selectedPayor.payor_name,
        },
      }));
    }
  };

  const handleServiceLineChange = <K extends keyof ClaimLine>(
    index: number,
    field: K,
    value: ClaimLine[K]
  ) => {
    setFormData(prev => {
      const newServiceLines = [...prev.claim_lines];
      newServiceLines[index] = {
        ...newServiceLines[index],
        [field]: value,
      };

      // Recalculate total charge if charge amount changes
      if (field === 'line_chrg_amt') {
        const totalCharge = newServiceLines.reduce(
          (sum, line) => sum + (parseFloat(line.line_chrg_amt) || 0),
          0
        );
        return {
          ...prev,
          claim_lines: newServiceLines,
          claim_header: {
            ...prev.claim_header,
            claim_amount: totalCharge.toFixed(2),
          },
        };
      }

      return {
        ...prev,
        claim_lines: newServiceLines,
      };
    });
  };

  const addServiceLine = () => {
    setFormData(prev => ({
      ...prev,
      claim_lines: [
        ...prev.claim_lines,
        {
          claim_line_number: (prev.claim_lines.length + 1).toString(),
          service_dates: [
            {
              date_cd: '472',
              date_format: 'RD8',
              date:
                prev.claim_header.claim_dates.find(d => d.date_cd === '434')
                  ?.date || '',
            },
          ],
          units: '1',
          units_measurement: 'UN',
          line_chrg_amt: '0',
          prcdr_cd: '',
          prcdr_cd_type: 'HC',
          modifier_cds: undefined as string | undefined,
          revenue_cd: '',
        },
      ],
    }));
  };

  const removeServiceLine = (index: number) => {
    setFormData(prev => ({
      ...prev,
      claim_lines: prev.claim_lines.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setX12Response(null);
    setResponseError(null);
    // Reset submission and acknowledgment states
    setSubmissionResponse(null);
    setAckResponse(null);

    try {
      // Validate service lines
      const invalidServiceLines = formData.claim_lines.filter(line => {
        // Revenue code is always required
        if (!line.revenue_cd) {
          return true;
        }
        // Some revenue codes like room and board don't require procedure codes
        const roomAndBoardCodes = [
          '0111',
          '0112',
          '0113',
          '0114',
          '0115',
          '0116',
          '0117',
          '0118',
          '0119',
        ];
        if (!roomAndBoardCodes.includes(line.revenue_cd) && !line.prcdr_cd) {
          return true;
        }
        return false;
      });

      if (invalidServiceLines.length > 0) {
        throw new Error(
          'Please fill in all required fields for service lines. Revenue code is required, and procedure code is required for non-room and board services.'
        );
      }

      // Send the form data directly as it matches the Claim837I structure
      const response = await fetch(`${API_BASE_URL}/x12/837i/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Handle Pydantic validation errors
        if (response.status === 422 && errorData.detail) {
          // Format validation errors
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail
                .map((err: ValidationError) => {
                  const field = err.loc?.join('.') || 'Unknown field';
                  return `${field}: ${err.msg}`;
                })
                .join('\n')
            : errorData.detail;
          throw new Error(`Validation Error:\n${validationErrors}`);
        }
        throw new Error(
          errorData.detail ||
            `Failed to generate X12 message: ${response.statusText}`
        );
      }

      const data = await response.json();
      setX12Response(data);
      success('X12 837I message generated successfully!');
    } catch (err) {
      console.error('Error:', err);
      const errorMessage = formatErrorMessage(err);
      setResponseError(errorMessage);
      error('Failed to generate X12 837I message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitToWaystar = async () => {
    if (!x12Response) {
      error('Please generate an X12 message first');
      return;
    }

    setIsSubmitting(true);
    setSubmissionResponse(null);
    setAckResponse(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/waystar/claims/submit/837i`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            x12_message: x12Response.x12_message,
            control_number: x12Response.control_number,
            test_mode: true, // Always use test mode for now
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // Handle Pydantic validation errors
        if (response.status === 422 && errorData.detail) {
          const validationErrors = Array.isArray(errorData.detail)
            ? errorData.detail
                .map((err: ValidationError) => {
                  const field = err.loc?.join('.') || 'Unknown field';
                  return `${field}: ${err.msg}`;
                })
                .join('\n')
            : errorData.detail;
          throw new Error(`Validation Error:\n${validationErrors}`);
        }
        throw new Error(
          errorData.detail || `Failed to submit claim: ${response.statusText}`
        );
      }

      const data: ClaimSubmissionResponse = await response.json();
      setSubmissionResponse(data);
      success('Claim submitted successfully to Waystar!');
    } catch (err) {
      console.error('Error:', err);
      error(
        err instanceof Error ? err.message : 'Failed to submit claim to Waystar'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckAcknowledgment = async () => {
    if (!submissionResponse) {
      error('Please submit a claim first');
      return;
    }

    setIsCheckingAck(true);
    setAckResponse(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/waystar/claims/${submissionResponse.claim_id}/ack`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // Handle different error formats
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            const validationErrors = errorData.detail
              .map((err: ValidationError) => {
                const field = err.loc?.join('.') || 'Unknown field';
                return `${field}: ${err.msg}`;
              })
              .join('\n');
            throw new Error(`Validation Error:\n${validationErrors}`);
          } else {
            // Simple error message
            throw new Error(errorData.detail);
          }
        }
        throw new Error(
          `Failed to check acknowledgment: ${response.statusText}`
        );
      }

      const data: AcknowledgmentResponse = await response.json();
      setAckResponse(data);

      if (data.status === 'found') {
        success('999 Acknowledgment found!');
      } else if (data.status === 'processing') {
        success('Claim is still being processed. Check again later.');
      } else {
        error('Acknowledgment not found');
      }
    } catch (err) {
      console.error('Error:', err);
      error(
        err instanceof Error ? err.message : 'Failed to check acknowledgment'
      );
    } finally {
      setIsCheckingAck(false);
    }
  };

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Input Form */}
        <Card className='max-h-[calc(100vh-200px)] overflow-y-auto'>
          <CardHeader>
            <CardTitle>X12 837I Claim Information</CardTitle>
            <CardDescription>
              Enter institutional claim details to generate X12 837I message
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-6'>
            {/* Submitter Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Submitter Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='submitterName'
                    className='block text-sm font-medium mb-2'
                  >
                    Submitter Name
                  </label>
                  <Input
                    id='submitterName'
                    value={formData.submitter.name}
                    onChange={e =>
                      handleSubmitterChange('name', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='submitterId'
                    className='block text-sm font-medium mb-2'
                  >
                    Submitter ID
                  </label>
                  <Input
                    id='submitterId'
                    value={formData.submitter.identifier || ''}
                    onChange={e =>
                      handleSubmitterChange('identifier', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='submitterContactName'
                    className='block text-sm font-medium mb-2'
                  >
                    Contact Name
                  </label>
                  <Input
                    id='submitterContactName'
                    value={formData.submitter.sbmtter_contact_name || ''}
                    onChange={e =>
                      handleSubmitterChange(
                        'sbmtter_contact_name',
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='submitterPhone'
                    className='block text-sm font-medium mb-2'
                  >
                    Contact Phone
                  </label>
                  <Input
                    id='submitterPhone'
                    value={
                      formData.submitter.sbmtter_contacts.find(
                        c => c[0] === 'TE'
                      )?.[1] || ''
                    }
                    onChange={e => {
                      const contacts: [string, string][] = [
                        ...formData.submitter.sbmtter_contacts,
                      ];
                      const phoneIndex = contacts.findIndex(c => c[0] === 'TE');
                      if (phoneIndex >= 0) {
                        contacts[phoneIndex] = ['TE', e.target.value];
                      } else {
                        contacts.push(['TE', e.target.value]);
                      }
                      handleSubmitterChange('sbmtter_contacts', contacts);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Receiver Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Receiver Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='receiverName'
                    className='block text-sm font-medium mb-2'
                  >
                    Receiver Name
                  </label>
                  <Input
                    id='receiverName'
                    value={formData.receiver.name}
                    onChange={e => handleReceiverChange('name', e.target.value)}
                  />
                </div>
                <div>
                  <label
                    htmlFor='receiverId'
                    className='block text-sm font-medium mb-2'
                  >
                    Receiver ID
                  </label>
                  <Input
                    id='receiverId'
                    value={formData.receiver.identifier || ''}
                    onChange={e =>
                      handleReceiverChange('identifier', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Billing Provider */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Billing Provider</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='billingEntityType'
                    className='block text-sm font-medium mb-2'
                  >
                    Entity Type
                  </label>
                  <Select
                    value={formData.providers.billing.entity_type}
                    onValueChange={value =>
                      handleBillingProviderChange(
                        'entity_type',
                        value as 'Individual' | 'Organization'
                      )
                    }
                  >
                    <SelectTrigger id='billingEntityType'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Individual'>Individual</SelectItem>
                      <SelectItem value='Organization'>Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='providerNpi'
                    className='block text-sm font-medium mb-2'
                  >
                    NPI
                  </label>
                  <NpiInput
                    id='providerNpi'
                    value={formData.providers.billing.npi || ''}
                    onValueChange={v =>
                      handleBillingProviderChange('npi', v || undefined)
                    }
                  />
                </div>
                <div className='col-span-2'>
                  <label
                    htmlFor='providerName'
                    className='block text-sm font-medium mb-2'
                  >
                    Provider Name
                  </label>
                  <Input
                    id='providerName'
                    value={formData.providers.billing.name || ''}
                    onChange={e =>
                      handleBillingProviderChange('name', e.target.value)
                    }
                  />
                </div>
                <div className='col-span-2'>
                  <label
                    htmlFor='providerStreet'
                    className='block text-sm font-medium mb-2'
                  >
                    Street Address
                  </label>
                  <Input
                    id='providerStreet'
                    value={formData.providers.billing.street || ''}
                    onChange={e =>
                      handleBillingProviderChange('street', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerCity'
                    className='block text-sm font-medium mb-2'
                  >
                    City
                  </label>
                  <Input
                    id='providerCity'
                    value={formData.providers.billing.city || ''}
                    onChange={e =>
                      handleBillingProviderChange('city', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerState'
                    className='block text-sm font-medium mb-2'
                  >
                    State
                  </label>
                  <Input
                    id='providerState'
                    value={formData.providers.billing.state || ''}
                    onChange={e =>
                      handleBillingProviderChange('state', e.target.value)
                    }
                    maxLength={2}
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerZip'
                    className='block text-sm font-medium mb-2'
                  >
                    ZIP Code
                  </label>
                  <Input
                    id='providerZip'
                    value={formData.providers.billing.zip || ''}
                    onChange={e =>
                      handleBillingProviderChange('zip', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerEinType'
                    className='block text-sm font-medium mb-2'
                  >
                    EIN Type
                  </label>
                  <Input
                    id='providerEinType'
                    value={formData.providers.billing.ein_type || ''}
                    onChange={e =>
                      handleBillingProviderChange('ein_type', e.target.value)
                    }
                    placeholder='e.g., EI'
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerEin'
                    className='block text-sm font-medium mb-2'
                  >
                    Tax ID (EIN)
                  </label>
                  <Input
                    id='providerEin'
                    value={formData.providers.billing.ein || ''}
                    onChange={e =>
                      handleBillingProviderChange('ein', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerTaxonomy'
                    className='block text-sm font-medium mb-2'
                  >
                    Taxonomy Code
                  </label>
                  <Input
                    id='providerTaxonomy'
                    value={formData.providers.billing.taxonomy || ''}
                    onChange={e =>
                      handleBillingProviderChange('taxonomy', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerRole'
                    className='block text-sm font-medium mb-2'
                  >
                    Provider Role
                  </label>
                  <Input
                    id='providerRole'
                    value={formData.providers.billing.provider_role || ''}
                    onChange={e =>
                      handleBillingProviderChange(
                        'provider_role',
                        e.target.value
                      )
                    }
                    placeholder='e.g., BI'
                  />
                </div>
              </div>
            </div>

            {/* Service Facility */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Service Facility</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='facilityEntityType'
                    className='block text-sm font-medium mb-2'
                  >
                    Entity Type
                  </label>
                  <Select
                    value={formData.providers.service_facility.entity_type}
                    onValueChange={value =>
                      handleServiceFacilityChange(
                        'entity_type',
                        value as 'Individual' | 'Organization'
                      )
                    }
                  >
                    <SelectTrigger id='facilityEntityType'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Individual'>Individual</SelectItem>
                      <SelectItem value='Organization'>Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='facilityNPI'
                    className='block text-sm font-medium mb-2'
                  >
                    NPI
                  </label>
                  <NpiInput
                    id='facilityNPI'
                    value={formData.providers.service_facility.npi || ''}
                    onValueChange={v =>
                      handleServiceFacilityChange('npi', v || undefined)
                    }
                  />
                </div>
                <div className='col-span-2'>
                  <label
                    htmlFor='facilityName'
                    className='block text-sm font-medium mb-2'
                  >
                    Facility Name
                  </label>
                  <Input
                    id='facilityName'
                    value={formData.providers.service_facility.name || ''}
                    onChange={e =>
                      handleServiceFacilityChange('name', e.target.value)
                    }
                  />
                </div>
                <div className='col-span-2'>
                  <label
                    htmlFor='facilityStreet'
                    className='block text-sm font-medium mb-2'
                  >
                    Street Address
                  </label>
                  <Input
                    id='facilityStreet'
                    value={formData.providers.service_facility.street || ''}
                    onChange={e =>
                      handleServiceFacilityChange('street', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='facilityCity'
                    className='block text-sm font-medium mb-2'
                  >
                    City
                  </label>
                  <Input
                    id='facilityCity'
                    value={formData.providers.service_facility.city || ''}
                    onChange={e =>
                      handleServiceFacilityChange('city', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='facilityState'
                    className='block text-sm font-medium mb-2'
                  >
                    State
                  </label>
                  <Input
                    id='facilityState'
                    value={formData.providers.service_facility.state || ''}
                    onChange={e =>
                      handleServiceFacilityChange('state', e.target.value)
                    }
                    maxLength={2}
                  />
                </div>
                <div>
                  <label
                    htmlFor='facilityZip'
                    className='block text-sm font-medium mb-2'
                  >
                    ZIP Code
                  </label>
                  <Input
                    id='facilityZip'
                    value={formData.providers.service_facility.zip || ''}
                    onChange={e =>
                      handleServiceFacilityChange('zip', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Attending Provider */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Attending Provider</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='attendingEntityType'
                    className='block text-sm font-medium mb-2'
                  >
                    Entity Type
                  </label>
                  <Select
                    value={formData.providers.attending.entity_type}
                    onValueChange={value => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          attending: {
                            ...prev.providers.attending,
                            entity_type: value as 'Individual' | 'Organization',
                          },
                        },
                      }));
                    }}
                  >
                    <SelectTrigger id='attendingEntityType'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Individual'>Individual</SelectItem>
                      <SelectItem value='Organization'>Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='attendingNPI'
                    className='block text-sm font-medium mb-2'
                  >
                    NPI
                  </label>
                  <NpiInput
                    id='attendingNPI'
                    value={formData.providers.attending.npi || ''}
                    onValueChange={v => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          attending: {
                            ...prev.providers.attending,
                            npi: v || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div className='col-span-2'>
                  <label
                    htmlFor='attendingName'
                    className='block text-sm font-medium mb-2'
                  >
                    Name
                  </label>
                  <Input
                    id='attendingName'
                    value={formData.providers.attending.name || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          attending: {
                            ...prev.providers.attending,
                            name: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='attendingEinType'
                    className='block text-sm font-medium mb-2'
                  >
                    EIN Type
                  </label>
                  <Input
                    id='attendingEinType'
                    value={formData.providers.attending.ein_type || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          attending: {
                            ...prev.providers.attending,
                            ein_type: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                    placeholder='e.g., EI'
                  />
                </div>
                <div>
                  <label
                    htmlFor='attendingEin'
                    className='block text-sm font-medium mb-2'
                  >
                    EIN
                  </label>
                  <Input
                    id='attendingEin'
                    value={formData.providers.attending.ein || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          attending: {
                            ...prev.providers.attending,
                            ein: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='attendingTaxonomy'
                    className='block text-sm font-medium mb-2'
                  >
                    Taxonomy Code
                  </label>
                  <Input
                    id='attendingTaxonomy'
                    value={formData.providers.attending.taxonomy || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          attending: {
                            ...prev.providers.attending,
                            taxonomy: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='attendingProviderRole'
                    className='block text-sm font-medium mb-2'
                  >
                    Provider Role
                  </label>
                  <Input
                    id='attendingProviderRole'
                    value={formData.providers.attending.provider_role || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          attending: {
                            ...prev.providers.attending,
                            provider_role: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Operating Provider */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Operating Provider</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='operatingEntityType'
                    className='block text-sm font-medium mb-2'
                  >
                    Entity Type
                  </label>
                  <Select
                    value={formData.providers.operating.entity_type}
                    onValueChange={value => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          operating: {
                            ...prev.providers.operating,
                            entity_type: value as 'Individual' | 'Organization',
                          },
                        },
                      }));
                    }}
                  >
                    <SelectTrigger id='operatingEntityType'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Individual'>Individual</SelectItem>
                      <SelectItem value='Organization'>Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='operatingNPI'
                    className='block text-sm font-medium mb-2'
                  >
                    NPI
                  </label>
                  <NpiInput
                    id='operatingNPI'
                    value={formData.providers.operating.npi || ''}
                    onValueChange={v => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          operating: {
                            ...prev.providers.operating,
                            npi: v || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div className='col-span-2'>
                  <label
                    htmlFor='operatingName'
                    className='block text-sm font-medium mb-2'
                  >
                    Name
                  </label>
                  <Input
                    id='operatingName'
                    value={formData.providers.operating.name || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          operating: {
                            ...prev.providers.operating,
                            name: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='operatingEinType'
                    className='block text-sm font-medium mb-2'
                  >
                    EIN Type
                  </label>
                  <Input
                    id='operatingEinType'
                    value={formData.providers.operating.ein_type || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          operating: {
                            ...prev.providers.operating,
                            ein_type: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                    placeholder='e.g., EI'
                  />
                </div>
                <div>
                  <label
                    htmlFor='operatingEin'
                    className='block text-sm font-medium mb-2'
                  >
                    EIN
                  </label>
                  <Input
                    id='operatingEin'
                    value={formData.providers.operating.ein || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          operating: {
                            ...prev.providers.operating,
                            ein: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='operatingTaxonomy'
                    className='block text-sm font-medium mb-2'
                  >
                    Taxonomy Code
                  </label>
                  <Input
                    id='operatingTaxonomy'
                    value={formData.providers.operating.taxonomy || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          operating: {
                            ...prev.providers.operating,
                            taxonomy: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='operatingProviderRole'
                    className='block text-sm font-medium mb-2'
                  >
                    Provider Role
                  </label>
                  <Input
                    id='operatingProviderRole'
                    value={formData.providers.operating.provider_role || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        providers: {
                          ...prev.providers,
                          operating: {
                            ...prev.providers.operating,
                            provider_role: e.target.value || undefined,
                          },
                        },
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Subscriber Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Subscriber Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='subscriberId'
                    className='block text-sm font-medium mb-2'
                  >
                    Member ID
                  </label>
                  <Input
                    id='subscriberId'
                    value={formData.subscriber.subscriber_identifier}
                    onChange={e =>
                      handleSubscriberChange(
                        'subscriber_identifier',
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='subscriberName'
                    className='block text-sm font-medium mb-2'
                  >
                    Full Name
                  </label>
                  <Input
                    id='subscriberName'
                    value={formData.subscriber.name}
                    onChange={e =>
                      handleSubscriberChange('name', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientRelationshipCd'
                    className='block text-sm font-medium mb-2'
                  >
                    Patient Relationship Code
                  </label>
                  <Input
                    id='patientRelationshipCd'
                    value={formData.subscriber.patient_relationship_cd || ''}
                    onChange={e =>
                      handleSubscriberChange(
                        'patient_relationship_cd',
                        e.target.value || undefined
                      )
                    }
                    placeholder='e.g., 01'
                  />
                </div>
                <div>
                  <label
                    htmlFor='subscriberRelationshipCd'
                    className='block text-sm font-medium mb-2'
                  >
                    Subscriber Relationship Code
                  </label>
                  <Select
                    value={formData.subscriber.subscriber_relationship_cd}
                    onValueChange={value =>
                      handleSubscriberChange(
                        'subscriber_relationship_cd',
                        value
                      )
                    }
                  >
                    <SelectTrigger id='subscriberRelationshipCd'>
                      <SelectValue placeholder='Select relationship...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='18'>18 - Self</SelectItem>
                      <SelectItem value='01'>01 - Spouse</SelectItem>
                      <SelectItem value='19'>19 - Child</SelectItem>
                      <SelectItem value='20'>20 - Employee</SelectItem>
                      <SelectItem value='21'>21 - Unknown</SelectItem>
                      <SelectItem value='39'>39 - Organ Donor</SelectItem>
                      <SelectItem value='40'>40 - Cadaver Donor</SelectItem>
                      <SelectItem value='53'>53 - Life Partner</SelectItem>
                      <SelectItem value='G8'>
                        G8 - Other Relationship
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='subscriberDob'
                    className='block text-sm font-medium mb-2'
                  >
                    Date of Birth
                  </label>
                  <Input
                    id='subscriberDob'
                    type='date'
                    value={formData.subscriber.dob.replace(
                      /(\d{4})(\d{2})(\d{2})/,
                      '$1-$2-$3'
                    )}
                    onChange={e =>
                      handleSubscriberChange(
                        'dob',
                        e.target.value.replace(/-/g, '')
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='subscriberGender'
                    className='block text-sm font-medium mb-2'
                  >
                    Gender
                  </label>
                  <Select
                    value={formData.subscriber.gender_cd}
                    onValueChange={(value: 'M' | 'F') =>
                      handleSubscriberChange('gender_cd', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='M'>Male</SelectItem>
                      <SelectItem value='F'>Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='subscriberMrn'
                    className='block text-sm font-medium mb-2'
                  >
                    Medical Record Number
                  </label>
                  <Input
                    id='subscriberMrn'
                    value={formData.subscriber.mrn}
                    onChange={e =>
                      handleSubscriberChange('mrn', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='subscriberStreet'
                    className='block text-sm font-medium mb-2'
                  >
                    Street Address
                  </label>
                  <Input
                    id='subscriberStreet'
                    value={formData.subscriber.street}
                    onChange={e =>
                      handleSubscriberChange('street', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='subscriberCity'
                    className='block text-sm font-medium mb-2'
                  >
                    City
                  </label>
                  <Input
                    id='subscriberCity'
                    value={formData.subscriber.city}
                    onChange={e =>
                      handleSubscriberChange('city', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='subscriberState'
                    className='block text-sm font-medium mb-2'
                  >
                    State
                  </label>
                  <Input
                    id='subscriberState'
                    value={formData.subscriber.state}
                    onChange={e =>
                      handleSubscriberChange('state', e.target.value)
                    }
                    maxLength={2}
                  />
                </div>
                <div>
                  <label
                    htmlFor='subscriberZip'
                    className='block text-sm font-medium mb-2'
                  >
                    ZIP Code
                  </label>
                  <Input
                    id='subscriberZip'
                    value={formData.subscriber.zip}
                    onChange={e =>
                      handleSubscriberChange('zip', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='groupNumber'
                    className='block text-sm font-medium mb-2'
                  >
                    Group Number
                  </label>
                  <Input
                    id='groupNumber'
                    value={formData.subscriber.group_number || ''}
                    onChange={e =>
                      handleSubscriberChange(
                        'group_number',
                        e.target.value || undefined
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='groupName'
                    className='block text-sm font-medium mb-2'
                  >
                    Group Name
                  </label>
                  <Input
                    id='groupName'
                    value={formData.subscriber.group_name || ''}
                    onChange={e =>
                      handleSubscriberChange(
                        'group_name',
                        e.target.value || undefined
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='insuranceTypeCode'
                    className='block text-sm font-medium mb-2'
                  >
                    Insurance Type Code
                  </label>
                  <Input
                    id='insuranceTypeCode'
                    value={formData.subscriber.insurance_type_code || ''}
                    onChange={e =>
                      handleSubscriberChange(
                        'insurance_type_code',
                        e.target.value || undefined
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='cobCode'
                    className='block text-sm font-medium mb-2'
                  >
                    Coordination of Benefits Code
                  </label>
                  <Input
                    id='cobCode'
                    value={
                      formData.subscriber.coordination_of_benefits_code || ''
                    }
                    onChange={e =>
                      handleSubscriberChange(
                        'coordination_of_benefits_code',
                        e.target.value || undefined
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='yesNoCondition'
                    className='block text-sm font-medium mb-2'
                  >
                    Yes/No Condition
                  </label>
                  <Select
                    value={formData.subscriber.yes_no_condition || ''}
                    onValueChange={value =>
                      handleSubscriberChange(
                        'yes_no_condition',
                        value || undefined
                      )
                    }
                  >
                    <SelectTrigger id='yesNoCondition'>
                      <SelectValue placeholder='Select...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Y'>Yes</SelectItem>
                      <SelectItem value='N'>No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='employmentStatusCode'
                    className='block text-sm font-medium mb-2'
                  >
                    Employment Status Code
                  </label>
                  <Input
                    id='employmentStatusCode'
                    value={formData.subscriber.employment_status_code || ''}
                    onChange={e =>
                      handleSubscriberChange(
                        'employment_status_code',
                        e.target.value || undefined
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='claimFilingIndicatorCode'
                    className='block text-sm font-medium mb-2'
                  >
                    Claim Filing Indicator Code
                  </label>
                  <Select
                    value={
                      formData.subscriber.claim_filing_indicator_code || ''
                    }
                    onValueChange={value =>
                      handleSubscriberChange(
                        'claim_filing_indicator_code',
                        value || undefined
                      )
                    }
                  >
                    <SelectTrigger id='claimFilingIndicatorCode'>
                      <SelectValue placeholder='Select filing indicator...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='MC'>Medicaid</SelectItem>
                      <SelectItem value='MB'>Medicare Part B</SelectItem>
                      <SelectItem value='MA'>Medicare Part A</SelectItem>
                      <SelectItem value='CI'>Commercial Insurance</SelectItem>
                      <SelectItem value='BL'>Blue Cross/Blue Shield</SelectItem>
                      <SelectItem value='CH'>Champus</SelectItem>
                      <SelectItem value='HM'>
                        Health Maintenance Organization
                      </SelectItem>
                      <SelectItem value='WC'>Workers Compensation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Patient Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Patient Information</h3>
              <p className='text-sm text-gray-600'>
                Complete this section if the patient is different from the
                subscriber
              </p>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='patientSubscriberId'
                    className='block text-sm font-medium mb-2'
                  >
                    Patient's Subscriber ID
                  </label>
                  <Input
                    id='patientSubscriberId'
                    value={formData.patient.subscriber_identifier}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          subscriber_identifier: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientName'
                    className='block text-sm font-medium mb-2'
                  >
                    Patient Name
                  </label>
                  <Input
                    id='patientName'
                    value={formData.patient.name}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          name: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientPatientRelationshipCd'
                    className='block text-sm font-medium mb-2'
                  >
                    Patient Relationship Code
                  </label>
                  <Input
                    id='patientPatientRelationshipCd'
                    value={formData.patient.patient_relationship_cd || ''}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          patient_relationship_cd: e.target.value || undefined,
                        },
                      }));
                    }}
                    placeholder='e.g., 01'
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientSubscriberRelationshipCd'
                    className='block text-sm font-medium mb-2'
                  >
                    Subscriber Relationship Code
                  </label>
                  <Select
                    value={formData.patient.subscriber_relationship_cd}
                    onValueChange={value => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          subscriber_relationship_cd: value,
                        },
                      }));
                    }}
                  >
                    <SelectTrigger id='patientSubscriberRelationshipCd'>
                      <SelectValue placeholder='Select relationship...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='18'>18 - Self</SelectItem>
                      <SelectItem value='01'>01 - Spouse</SelectItem>
                      <SelectItem value='19'>19 - Child</SelectItem>
                      <SelectItem value='20'>20 - Employee</SelectItem>
                      <SelectItem value='21'>21 - Unknown</SelectItem>
                      <SelectItem value='39'>39 - Organ Donor</SelectItem>
                      <SelectItem value='40'>40 - Cadaver Donor</SelectItem>
                      <SelectItem value='53'>53 - Life Partner</SelectItem>
                      <SelectItem value='G8'>
                        G8 - Other Relationship
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='patientStreet'
                    className='block text-sm font-medium mb-2'
                  >
                    Street Address
                  </label>
                  <Input
                    id='patientStreet'
                    value={formData.patient.street}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          street: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientCity'
                    className='block text-sm font-medium mb-2'
                  >
                    City
                  </label>
                  <Input
                    id='patientCity'
                    value={formData.patient.city}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          city: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientState'
                    className='block text-sm font-medium mb-2'
                  >
                    State
                  </label>
                  <Input
                    id='patientState'
                    value={formData.patient.state}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          state: e.target.value,
                        },
                      }));
                    }}
                    maxLength={2}
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientZip'
                    className='block text-sm font-medium mb-2'
                  >
                    ZIP Code
                  </label>
                  <Input
                    id='patientZip'
                    value={formData.patient.zip}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          zip: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientDob'
                    className='block text-sm font-medium mb-2'
                  >
                    Date of Birth
                  </label>
                  <Input
                    id='patientDob'
                    value={formData.patient.dob}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          dob: e.target.value,
                        },
                      }));
                    }}
                    placeholder='YYYYMMDD'
                  />
                </div>
                <div>
                  <label
                    htmlFor='patientGender'
                    className='block text-sm font-medium mb-2'
                  >
                    Gender
                  </label>
                  <Select
                    value={formData.patient.gender_cd}
                    onValueChange={(value: 'M' | 'F') => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          gender_cd: value,
                        },
                      }));
                    }}
                  >
                    <SelectTrigger id='patientGender'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='M'>Male</SelectItem>
                      <SelectItem value='F'>Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='patientMrn'
                    className='block text-sm font-medium mb-2'
                  >
                    Medical Record Number
                  </label>
                  <Input
                    id='patientMrn'
                    value={formData.patient.mrn}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        patient: {
                          ...prev.patient,
                          mrn: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Payer Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Payer Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div className='col-span-2'>
                  <label
                    htmlFor='payor'
                    className='block text-sm font-medium mb-2'
                  >
                    Payor
                  </label>
                  <PayorCombobox
                    value={formData.payer.payer_identifier}
                    onValueChange={handlePayorSelect}
                    placeholder='Select a payor'
                    className='w-full'
                  />
                </div>
                <div>
                  <label
                    htmlFor='payerName'
                    className='block text-sm font-medium mb-2'
                  >
                    Payer Name
                  </label>
                  <Input
                    id='payerName'
                    value={formData.payer.payer_name}
                    onChange={e =>
                      handlePayerChange('payer_name', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='payerId'
                    className='block text-sm font-medium mb-2'
                  >
                    Payer ID
                  </label>
                  <Input
                    id='payerId'
                    value={formData.payer.payer_identifier}
                    onChange={e =>
                      handlePayerChange('payer_identifier', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='payerStreet'
                    className='block text-sm font-medium mb-2'
                  >
                    Payer Street Address
                  </label>
                  <Input
                    id='payerStreet'
                    value={formData.payer.street || ''}
                    onChange={e =>
                      handlePayerChange('street', e.target.value || undefined)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='payerCity'
                    className='block text-sm font-medium mb-2'
                  >
                    Payer City
                  </label>
                  <Input
                    id='payerCity'
                    value={formData.payer.city || ''}
                    onChange={e =>
                      handlePayerChange('city', e.target.value || undefined)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='payerState'
                    className='block text-sm font-medium mb-2'
                  >
                    Payer State
                  </label>
                  <Input
                    id='payerState'
                    value={formData.payer.state || ''}
                    onChange={e =>
                      handlePayerChange('state', e.target.value || undefined)
                    }
                    maxLength={2}
                  />
                </div>
                <div>
                  <label
                    htmlFor='payerZip'
                    className='block text-sm font-medium mb-2'
                  >
                    Payer ZIP Code
                  </label>
                  <Input
                    id='payerZip'
                    value={formData.payer.zip || ''}
                    onChange={e =>
                      handlePayerChange('zip', e.target.value || undefined)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Claim Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Claim Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='claimId'
                    className='block text-sm font-medium mb-2'
                  >
                    Claim ID
                  </label>
                  <Input
                    id='claimId'
                    value={formData.claim_header.claim_id}
                    onChange={e =>
                      handleClaimHeaderChange('claim_id', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='totalCharge'
                    className='block text-sm font-medium mb-2'
                  >
                    Total Charge
                  </label>
                  <Input
                    id='totalCharge'
                    type='number'
                    step='0.01'
                    value={formData.claim_header.claim_amount}
                    onChange={e =>
                      handleClaimHeaderChange('claim_amount', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='facilityCode'
                    className='block text-sm font-medium mb-2'
                  >
                    Facility Code
                  </label>
                  <Select
                    value={formData.claim_header.facility_type_code}
                    onValueChange={value =>
                      handleClaimHeaderChange('facility_type_code', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {facilityCodeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='admissionType'
                    className='block text-sm font-medium mb-2'
                  >
                    Admission Type
                  </label>
                  <Select
                    value={formData.claim_header.admission_type}
                    onValueChange={value =>
                      handleClaimHeaderChange('admission_type', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select admission type' />
                    </SelectTrigger>
                    <SelectContent>
                      {admissionTypeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='admissionSource'
                    className='block text-sm font-medium mb-2'
                  >
                    Admission Source
                  </label>
                  <Select
                    value={formData.claim_header.admission_src_cd}
                    onValueChange={value =>
                      handleClaimHeaderChange('admission_src_cd', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select admission source' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='1'>Physician referral</SelectItem>
                      <SelectItem value='2'>Clinic referral</SelectItem>
                      <SelectItem value='3'>HMO referral</SelectItem>
                      <SelectItem value='4'>Transfer from hospital</SelectItem>
                      <SelectItem value='5'>Transfer from SNF</SelectItem>
                      <SelectItem value='6'>
                        Transfer from another health care facility
                      </SelectItem>
                      <SelectItem value='7'>Emergency room</SelectItem>
                      <SelectItem value='8'>Court/law enforcement</SelectItem>
                      <SelectItem value='9'>
                        Information not available
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='dischargeStatus'
                    className='block text-sm font-medium mb-2'
                  >
                    Discharge Status
                  </label>
                  <Select
                    value={formData.claim_header.discharge_status_cd}
                    onValueChange={value =>
                      handleClaimHeaderChange('discharge_status_cd', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select discharge status' />
                    </SelectTrigger>
                    <SelectContent>
                      {patientStatusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='drgCode'
                    className='block text-sm font-medium mb-2'
                  >
                    DRG Code
                  </label>
                  <Input
                    id='drgCode'
                    value={formData.claim_header.drg_cd || ''}
                    onChange={e =>
                      handleClaimHeaderChange('drg_cd', e.target.value)
                    }
                    placeholder='e.g., DR:885'
                  />
                </div>
                <div>
                  <label
                    htmlFor='encounterID'
                    className='block text-sm font-medium mb-2'
                  >
                    Encounter ID
                  </label>
                  <Input
                    id='encounterID'
                    value={formData.claim_header.encounter_id || ''}
                    onChange={e =>
                      handleClaimHeaderChange(
                        'encounter_id',
                        e.target.value || undefined
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='frequencyCode'
                    className='block text-sm font-medium mb-2'
                  >
                    Frequency Code
                  </label>
                  <Select
                    value={formData.claim_header.frequency_code || ''}
                    onValueChange={value =>
                      handleClaimHeaderChange(
                        'frequency_code',
                        value || undefined
                      )
                    }
                  >
                    <SelectTrigger id='frequencyCode'>
                      <SelectValue placeholder='Select frequency...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='1'>
                        Admit through discharge claim
                      </SelectItem>
                      <SelectItem value='7'>
                        Replacement of prior claim
                      </SelectItem>
                      <SelectItem value='8'>
                        Void/cancel of prior claim
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='providerAcceptAssignment'
                    className='block text-sm font-medium mb-2'
                  >
                    Provider Accept Assignment
                  </label>
                  <Select
                    value={
                      formData.claim_header.provider_accept_assignment || ''
                    }
                    onValueChange={value =>
                      handleClaimHeaderChange(
                        'provider_accept_assignment',
                        value || undefined
                      )
                    }
                  >
                    <SelectTrigger id='providerAcceptAssignment'>
                      <SelectValue placeholder='Select...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Y'>Yes</SelectItem>
                      <SelectItem value='N'>No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='assignmentOfBenefits'
                    className='block text-sm font-medium mb-2'
                  >
                    Assignment of Benefits
                  </label>
                  <Select
                    value={formData.claim_header.assignment_of_benefits || ''}
                    onValueChange={value =>
                      handleClaimHeaderChange(
                        'assignment_of_benefits',
                        value || undefined
                      )
                    }
                  >
                    <SelectTrigger id='assignmentOfBenefits'>
                      <SelectValue placeholder='Select...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Y'>Yes</SelectItem>
                      <SelectItem value='N'>No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Claim References */}
              <div className='mt-4'>
                <h4 className='text-md font-semibold mb-2'>Claim References</h4>
                <div className='space-y-2'>
                  {formData.claim_header.clm_refs.map((ref, index) => (
                    <div key={index} className='flex gap-2 items-end'>
                      <div className='flex-1'>
                        <label className='block text-sm font-medium mb-1'>
                          Reference Qualifier
                        </label>
                        <Input
                          value={ref.id_code_qualifier}
                          onChange={e => {
                            const newRefs = [...formData.claim_header.clm_refs];
                            newRefs[index] = {
                              ...ref,
                              id_code_qualifier: e.target.value,
                            };
                            handleClaimHeaderChange('clm_refs', newRefs);
                          }}
                          placeholder='e.g., D9'
                        />
                      </div>
                      <div className='flex-1'>
                        <label className='block text-sm font-medium mb-1'>
                          Reference ID
                        </label>
                        <Input
                          value={ref.id}
                          onChange={e => {
                            const newRefs = [...formData.claim_header.clm_refs];
                            newRefs[index] = { ...ref, id: e.target.value };
                            handleClaimHeaderChange('clm_refs', newRefs);
                          }}
                          placeholder='e.g., TD-R192ICE00087'
                        />
                      </div>
                      <Button
                        onClick={() => {
                          const newRefs = formData.claim_header.clm_refs.filter(
                            (_, i) => i !== index
                          );
                          handleClaimHeaderChange('clm_refs', newRefs);
                        }}
                        size='sm'
                        variant='destructive'
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() => {
                      const newRefs = [
                        ...formData.claim_header.clm_refs,
                        { id_code_qualifier: '', id: '' },
                      ];
                      handleClaimHeaderChange('clm_refs', newRefs);
                    }}
                    size='sm'
                    variant='outline'
                  >
                    Add Reference
                  </Button>
                </div>
              </div>

              {/* Value Codes */}
              <div className='mt-4'>
                <h4 className='text-md font-semibold mb-2'>Value Codes</h4>
                <div className='space-y-2'>
                  {(formData.claim_header.value_codes || []).map(
                    (vc, index) => (
                      <div key={index} className='flex gap-2 items-end'>
                        <div className='w-24'>
                          <label className='block text-sm font-medium mb-1'>
                            Qualifier
                          </label>
                          <Input
                            value={vc.code_qualifier}
                            onChange={e => {
                              const newCodes = [
                                ...(formData.claim_header.value_codes || []),
                              ];
                              newCodes[index] = {
                                ...vc,
                                code_qualifier: e.target.value,
                              };
                              handleClaimHeaderChange('value_codes', newCodes);
                            }}
                            placeholder='BE'
                          />
                        </div>
                        <div className='w-24'>
                          <label className='block text-sm font-medium mb-1'>
                            Code
                          </label>
                          <Input
                            value={vc.code}
                            onChange={e => {
                              const newCodes = [
                                ...(formData.claim_header.value_codes || []),
                              ];
                              newCodes[index] = { ...vc, code: e.target.value };
                              handleClaimHeaderChange('value_codes', newCodes);
                            }}
                            placeholder='01'
                          />
                        </div>
                        <div className='flex-1'>
                          <label className='block text-sm font-medium mb-1'>
                            Amount
                          </label>
                          <Input
                            value={vc.amount || ''}
                            onChange={e => {
                              const newCodes = [
                                ...(formData.claim_header.value_codes || []),
                              ];
                              newCodes[index] = {
                                ...vc,
                                amount: e.target.value,
                              };
                              handleClaimHeaderChange('value_codes', newCodes);
                            }}
                            placeholder='1563'
                          />
                        </div>
                        <div className='flex-1'>
                          <label className='block text-sm font-medium mb-1'>
                            Quantity
                          </label>
                          <Input
                            value={vc.quantity || ''}
                            onChange={e => {
                              const newCodes = [
                                ...(formData.claim_header.value_codes || []),
                              ];
                              newCodes[index] = {
                                ...vc,
                                quantity: e.target.value,
                              };
                              handleClaimHeaderChange('value_codes', newCodes);
                            }}
                            placeholder='Optional'
                          />
                        </div>
                        <Button
                          onClick={() => {
                            const newCodes = (
                              formData.claim_header.value_codes || []
                            ).filter((_, i) => i !== index);
                            handleClaimHeaderChange('value_codes', newCodes);
                          }}
                          size='sm'
                          variant='destructive'
                        >
                          Remove
                        </Button>
                      </div>
                    )
                  )}
                  <Button
                    onClick={() => {
                      const newCodes = [
                        ...(formData.claim_header.value_codes || []),
                        {
                          code_qualifier: 'BE',
                          code: '',
                          amount: '',
                          quantity: '',
                        },
                      ];
                      handleClaimHeaderChange('value_codes', newCodes);
                    }}
                    size='sm'
                    variant='outline'
                  >
                    Add Value Code
                  </Button>
                </div>
              </div>

              {/* Claim Dates */}
              <div className='mt-4'>
                <h4 className='text-md font-semibold mb-2'>Claim Dates</h4>
                <div className='space-y-2'>
                  {formData.claim_header.claim_dates.map((date, index) => (
                    <div key={index} className='flex gap-2 items-end'>
                      <div className='w-32'>
                        <label className='block text-sm font-medium mb-1'>
                          Date Code
                        </label>
                        <Select
                          value={date.date_cd}
                          onValueChange={value => {
                            const newDates = [
                              ...formData.claim_header.claim_dates,
                            ];
                            newDates[index] = { ...date, date_cd: value };
                            handleClaimHeaderChange('claim_dates', newDates);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='096'>Discharge Hour</SelectItem>
                            <SelectItem value='434'>
                              Statement Period
                            </SelectItem>
                            <SelectItem value='435'>
                              Admission Date/Hour
                            </SelectItem>
                            <SelectItem value='439'>Accident Date</SelectItem>
                            <SelectItem value='452'>
                              Onset of Illness
                            </SelectItem>
                            <SelectItem value='472'>Service Period</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='w-32'>
                        <label className='block text-sm font-medium mb-1'>
                          Format
                        </label>
                        <Select
                          value={date.date_format}
                          onValueChange={value => {
                            const newDates = [
                              ...formData.claim_header.claim_dates,
                            ];
                            newDates[index] = { ...date, date_format: value };
                            handleClaimHeaderChange('claim_dates', newDates);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='D8'>Date (CCYYMMDD)</SelectItem>
                            <SelectItem value='TM'>Time (HHMM)</SelectItem>
                            <SelectItem value='DT'>
                              DateTime (CCYYMMDDHHMM)
                            </SelectItem>
                            <SelectItem value='RD8'>
                              Date Range (CCYYMMDD-CCYYMMDD)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='flex-1'>
                        <label className='block text-sm font-medium mb-1'>
                          Date Value
                        </label>
                        <Input
                          value={date.date}
                          onChange={e => {
                            const newDates = [
                              ...formData.claim_header.claim_dates,
                            ];
                            newDates[index] = { ...date, date: e.target.value };
                            handleClaimHeaderChange('claim_dates', newDates);
                          }}
                          placeholder={
                            date.date_format === 'RD8'
                              ? '20180628-20180702'
                              : date.date_format === 'TM'
                                ? '0900'
                                : date.date_format === 'DT'
                                  ? '201806280800'
                                  : '20180628'
                          }
                        />
                      </div>
                      <Button
                        onClick={() => {
                          const newDates =
                            formData.claim_header.claim_dates.filter(
                              (_, i) => i !== index
                            );
                          handleClaimHeaderChange('claim_dates', newDates);
                        }}
                        size='sm'
                        variant='destructive'
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() => {
                      const newDates = [
                        ...formData.claim_header.claim_dates,
                        { date_cd: '', date_format: 'D8', date: '' },
                      ];
                      handleClaimHeaderChange('claim_dates', newDates);
                    }}
                    size='sm'
                    variant='outline'
                  >
                    Add Claim Date
                  </Button>
                </div>
              </div>
            </div>

            {/* Diagnosis */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Diagnosis</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='principalDx'
                    className='block text-sm font-medium mb-2'
                  >
                    Principal Diagnosis Code
                  </label>
                  <Input
                    id='principalDx'
                    value={formData.diagnosis.principal_dx_cd}
                    onChange={e =>
                      handleDiagnosisChange('principal_dx_cd', e.target.value)
                    }
                    placeholder='e.g., F39'
                  />
                </div>
                <div>
                  <label
                    htmlFor='principalDxPoa'
                    className='block text-sm font-medium mb-2'
                  >
                    Principal Diagnosis POA
                  </label>
                  <Select
                    value={formData.diagnosis.principal_dx_poa}
                    onValueChange={value =>
                      handleDiagnosisChange('principal_dx_poa', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Y'>Y - Yes</SelectItem>
                      <SelectItem value='N'>N - No</SelectItem>
                      <SelectItem value='U'>U - Unknown</SelectItem>
                      <SelectItem value='W'>
                        W - Clinically undetermined
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='admittingDx'
                    className='block text-sm font-medium mb-2'
                  >
                    Admitting Diagnosis Code
                  </label>
                  <Input
                    id='admittingDx'
                    value={formData.diagnosis.admitting_dx_cd}
                    onChange={e =>
                      handleDiagnosisChange('admitting_dx_cd', e.target.value)
                    }
                    placeholder='e.g., F39'
                  />
                </div>
                <div>
                  <label
                    htmlFor='admittingDxPoa'
                    className='block text-sm font-medium mb-2'
                  >
                    Admitting Diagnosis POA
                  </label>
                  <Select
                    value={formData.diagnosis.admitting_dx_poa}
                    onValueChange={value =>
                      handleDiagnosisChange('admitting_dx_poa', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Y'>Y - Yes</SelectItem>
                      <SelectItem value='N'>N - No</SelectItem>
                      <SelectItem value='U'>U - Unknown</SelectItem>
                      <SelectItem value='W'>
                        W - Clinically undetermined
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='reasonVisitDx'
                    className='block text-sm font-medium mb-2'
                  >
                    Reason for Visit Diagnosis Code
                  </label>
                  <Input
                    id='reasonVisitDx'
                    value={formData.diagnosis.reason_visit_dx_cd || ''}
                    onChange={e =>
                      handleDiagnosisChange(
                        'reason_visit_dx_cd',
                        e.target.value || undefined
                      )
                    }
                    placeholder='e.g., R50.9'
                  />
                </div>
                <div>
                  <label
                    htmlFor='reasonVisitDxPoa'
                    className='block text-sm font-medium mb-2'
                  >
                    Reason for Visit POA
                  </label>
                  <Select
                    value={formData.diagnosis.reason_visit_dx_poa || ''}
                    onValueChange={value =>
                      handleDiagnosisChange(
                        'reason_visit_dx_poa',
                        value || undefined
                      )
                    }
                  >
                    <SelectTrigger id='reasonVisitDxPoa'>
                      <SelectValue placeholder='Select POA...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Y'>Y - Yes</SelectItem>
                      <SelectItem value='N'>N - No</SelectItem>
                      <SelectItem value='U'>U - Unknown</SelectItem>
                      <SelectItem value='W'>
                        W - Clinically undetermined
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='externalInjuryDx'
                    className='block text-sm font-medium mb-2'
                  >
                    External Injury Diagnosis Code
                  </label>
                  <Input
                    id='externalInjuryDx'
                    value={formData.diagnosis.external_injury_dx_cd || ''}
                    onChange={e =>
                      handleDiagnosisChange(
                        'external_injury_dx_cd',
                        e.target.value || undefined
                      )
                    }
                    placeholder='e.g., V89.2'
                  />
                </div>
                <div>
                  <label
                    htmlFor='externalInjuryDxPoa'
                    className='block text-sm font-medium mb-2'
                  >
                    External Injury POA
                  </label>
                  <Select
                    value={formData.diagnosis.external_injury_dx_poa || ''}
                    onValueChange={value =>
                      handleDiagnosisChange(
                        'external_injury_dx_poa',
                        value || undefined
                      )
                    }
                  >
                    <SelectTrigger id='externalInjuryDxPoa'>
                      <SelectValue placeholder='Select POA...' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Y'>Y - Yes</SelectItem>
                      <SelectItem value='N'>N - No</SelectItem>
                      <SelectItem value='U'>U - Unknown</SelectItem>
                      <SelectItem value='W'>
                        W - Clinically undetermined
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Other Diagnosis Codes */}
              <div className='mt-4'>
                <h4 className='text-md font-semibold mb-2'>
                  Other Diagnosis Codes
                </h4>
                <div className='space-y-2'>
                  {formData.diagnosis.other_dx_cds.map((dx, index) => (
                    <div key={index} className='flex gap-2 items-end'>
                      <div className='flex-1'>
                        <label className='block text-sm font-medium mb-1'>
                          Diagnosis Code {index + 1}
                        </label>
                        <Input
                          value={dx.dx_cd}
                          onChange={e => {
                            const newOtherDx = [
                              ...formData.diagnosis.other_dx_cds,
                            ];
                            newOtherDx[index] = {
                              ...dx,
                              dx_cd: e.target.value,
                            };
                            handleDiagnosisChange('other_dx_cds', newOtherDx);
                          }}
                          placeholder='e.g., F1319'
                        />
                      </div>
                      <div className='w-32'>
                        <label className='block text-sm font-medium mb-1'>
                          POA
                        </label>
                        <Select
                          value={dx.poa}
                          onValueChange={value => {
                            const newOtherDx = [
                              ...formData.diagnosis.other_dx_cds,
                            ];
                            newOtherDx[index] = { ...dx, poa: value };
                            handleDiagnosisChange('other_dx_cds', newOtherDx);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='Y'>Y</SelectItem>
                            <SelectItem value='N'>N</SelectItem>
                            <SelectItem value='U'>U</SelectItem>
                            <SelectItem value='W'>W</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={() => {
                          const newOtherDx =
                            formData.diagnosis.other_dx_cds.filter(
                              (_, i) => i !== index
                            );
                          handleDiagnosisChange('other_dx_cds', newOtherDx);
                        }}
                        size='sm'
                        variant='destructive'
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() => {
                      const newOtherDx = [
                        ...formData.diagnosis.other_dx_cds,
                        { dx_cd: '', poa: 'Y' },
                      ];
                      handleDiagnosisChange('other_dx_cds', newOtherDx);
                    }}
                    size='sm'
                    variant='outline'
                  >
                    Add Diagnosis Code
                  </Button>
                </div>
              </div>
            </div>

            {/* Service Lines */}
            <div className='space-y-4'>
              <div className='flex justify-between items-center'>
                <h3 className='text-lg font-semibold'>Service Lines</h3>
                <Button onClick={addServiceLine} size='sm'>
                  Add Service Line
                </Button>
              </div>
              {formData.claim_lines.map((line, index) => (
                <div key={index} className='border p-4 rounded-lg space-y-3'>
                  <div className='flex justify-between items-center'>
                    <h4 className='font-medium'>
                      Service Line {line.claim_line_number}
                    </h4>
                    {formData.claim_lines.length > 1 && (
                      <Button
                        onClick={() => removeServiceLine(index)}
                        size='sm'
                        variant='destructive'
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Revenue Code
                      </label>
                      <Select
                        value={line.revenue_cd}
                        onValueChange={value =>
                          handleServiceLineChange(index, 'revenue_cd', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select revenue code' />
                        </SelectTrigger>
                        <SelectContent>
                          {revenueCodeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Procedure Code
                      </label>
                      <Input
                        value={line.prcdr_cd}
                        onChange={e =>
                          handleServiceLineChange(
                            index,
                            'prcdr_cd',
                            e.target.value
                          )
                        }
                        placeholder='e.g., 99233'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Procedure Code Type
                      </label>
                      <Select
                        value={line.prcdr_cd_type}
                        onValueChange={value =>
                          handleServiceLineChange(index, 'prcdr_cd_type', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select code type' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='HC'>HCPCS/CPT</SelectItem>
                          <SelectItem value='HP'>HIPPS</SelectItem>
                          <SelectItem value='NU'>
                            National Uniform Billing Code
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Modifier Codes
                      </label>
                      <Input
                        value={line.modifier_cds || ''}
                        onChange={e =>
                          handleServiceLineChange(
                            index,
                            'modifier_cds',
                            e.target.value || undefined
                          )
                        }
                        placeholder='e.g., 25, 59'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Charge Amount
                      </label>
                      <Input
                        type='number'
                        step='0.01'
                        value={line.line_chrg_amt}
                        onChange={e =>
                          handleServiceLineChange(
                            index,
                            'line_chrg_amt',
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Units
                      </label>
                      <Input
                        type='number'
                        value={line.units}
                        onChange={e =>
                          handleServiceLineChange(
                            index,
                            'units',
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Unit Measurement
                      </label>
                      <Select
                        value={line.units_measurement}
                        onValueChange={value =>
                          handleServiceLineChange(
                            index,
                            'units_measurement',
                            value
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select unit' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='UN'>Unit</SelectItem>
                          <SelectItem value='DA'>Days</SelectItem>
                          <SelectItem value='MJ'>Minutes</SelectItem>
                          <SelectItem value='HR'>Hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Service Dates */}
                  <div className='mt-3'>
                    <h5 className='text-sm font-medium mb-2'>Service Dates</h5>
                    <div className='space-y-2'>
                      {line.service_dates.map((svcDate, svcIndex) => (
                        <div key={svcIndex} className='flex gap-2 items-end'>
                          <div className='w-32'>
                            <label className='block text-xs font-medium mb-1'>
                              Date Code
                            </label>
                            <Select
                              value={svcDate.date_cd}
                              onValueChange={value => {
                                const newLines = [...formData.claim_lines];
                                newLines[index].service_dates[svcIndex] = {
                                  ...svcDate,
                                  date_cd: value,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  claim_lines: newLines,
                                }));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='472'>
                                  Service Period
                                </SelectItem>
                                <SelectItem value='150'>
                                  Service Date
                                </SelectItem>
                                <SelectItem value='151'>
                                  Service From Date
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className='w-32'>
                            <label className='block text-xs font-medium mb-1'>
                              Format
                            </label>
                            <Select
                              value={svcDate.date_format}
                              onValueChange={value => {
                                const newLines = [...formData.claim_lines];
                                newLines[index].service_dates[svcIndex] = {
                                  ...svcDate,
                                  date_format: value,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  claim_lines: newLines,
                                }));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='D8'>Date</SelectItem>
                                <SelectItem value='RD8'>Date Range</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className='flex-1'>
                            <label className='block text-xs font-medium mb-1'>
                              Date Value
                            </label>
                            <Input
                              value={svcDate.date}
                              onChange={e => {
                                const newLines = [...formData.claim_lines];
                                newLines[index].service_dates[svcIndex] = {
                                  ...svcDate,
                                  date: e.target.value,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  claim_lines: newLines,
                                }));
                              }}
                              placeholder={
                                svcDate.date_format === 'RD8'
                                  ? '20180628-20180702'
                                  : '20180628'
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleSubmit}
              disabled={isLoading}
              className='w-full'
            >
              {isLoading ? 'Generating...' : 'Generate X12 837I Message'}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className='max-h-[calc(100vh-200px)] overflow-y-auto'>
          <CardHeader>
            <CardTitle>Generated X12 837I Message</CardTitle>
            <CardDescription>
              The generated X12 837I message will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            {responseError ? (
              <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
                <h4 className='font-semibold text-red-800'>Error</h4>
                <div className='text-red-600 whitespace-pre-wrap'>
                  {responseError}
                </div>
              </div>
            ) : x12Response ? (
              <>
                <div>
                  <label className='block text-sm font-medium mb-2'>
                    Transaction Type: {x12Response.transaction_type}
                  </label>
                  <label className='block text-sm font-medium mb-2'>
                    Control Number: {x12Response.control_number}
                  </label>
                </div>
                <div>
                  <label className='block text-sm font-medium mb-2'>
                    X12 837I Message
                  </label>
                  <Textarea
                    value={x12Response.x12_message}
                    readOnly
                    className='font-mono text-xs h-64'
                    placeholder='Generated X12 message will appear here...'
                  />
                </div>

                {/* Action Buttons */}
                <div className='flex gap-4'>
                  <Button
                    onClick={handleSubmitToWaystar}
                    disabled={!x12Response || isSubmitting}
                    variant='secondary'
                    className='flex-1'
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit to Waystar'}
                  </Button>
                  <Button
                    onClick={handleCheckAcknowledgment}
                    disabled={!submissionResponse || isCheckingAck}
                    variant='outline'
                    className='flex-1'
                  >
                    {isCheckingAck ? 'Checking...' : 'Check Acknowledgment'}
                  </Button>
                </div>

                {/* Submission Response */}
                {submissionResponse && (
                  <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
                    <h4 className='font-semibold text-green-800 mb-2'>
                      Submission Successful
                    </h4>
                    <div className='space-y-1 text-sm'>
                      <p>
                        <strong>Claim ID:</strong> {submissionResponse.claim_id}
                      </p>
                      <p>
                        <strong>Control Number:</strong>{' '}
                        {submissionResponse.control_number}
                      </p>
                      <p>
                        <strong>Remote Filename:</strong>{' '}
                        {submissionResponse.remote_filename}
                      </p>
                      <p>
                        <strong>Timestamp:</strong>{' '}
                        {new Date(
                          submissionResponse.submission_timestamp
                        ).toLocaleString()}
                      </p>
                      <p>
                        <strong>Message:</strong> {submissionResponse.message}
                      </p>
                    </div>
                  </div>
                )}

                {/* Acknowledgment Response */}
                {ackResponse && (
                  <div
                    className={`p-4 border rounded-lg ${
                      ackResponse.status === 'found'
                        ? 'bg-blue-50 border-blue-200'
                        : ackResponse.status === 'processing'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <h4
                      className={`font-semibold mb-2 ${
                        ackResponse.status === 'found'
                          ? 'text-blue-800'
                          : ackResponse.status === 'processing'
                            ? 'text-yellow-800'
                            : 'text-gray-800'
                      }`}
                    >
                      Acknowledgment Status:{' '}
                      {ackResponse.status.charAt(0).toUpperCase() +
                        ackResponse.status.slice(1)}
                    </h4>
                    <div className='space-y-1 text-sm'>
                      <p>
                        <strong>Message:</strong> {ackResponse.message}
                      </p>
                      {ackResponse.ack_filename && (
                        <p>
                          <strong>999 Filename:</strong>{' '}
                          {ackResponse.ack_filename}
                        </p>
                      )}
                      {ackResponse.ack_parsed && (
                        <div className='mt-3'>
                          <p className='font-semibold mb-1'>
                            999 Response Details:
                          </p>
                          <div className='bg-white p-3 rounded border'>
                            <p>
                              <strong>Status:</strong>
                              <span
                                className={`ml-2 font-semibold ${getStatusColor(ackResponse.ack_parsed.status)}`}
                              >
                                {getStatusText(
                                  ackResponse.ack_parsed.status.toUpperCase()
                                )}
                              </span>
                            </p>
                            {ackResponse.ack_parsed.status_description && (
                              <p className='text-sm mt-1'>
                                <strong>Description:</strong>{' '}
                                {ackResponse.ack_parsed.status_description}
                              </p>
                            )}
                            {ackResponse.ack_parsed.statistics && (
                              <div className='mt-2 text-sm'>
                                <p>
                                  <strong>Statistics:</strong>
                                </p>
                                <ul className='ml-4 text-gray-700'>
                                  <li>
                                    Total Segments:{' '}
                                    {
                                      ackResponse.ack_parsed.statistics
                                        .total_segments
                                    }
                                  </li>
                                  <li>
                                    Accepted:{' '}
                                    {
                                      ackResponse.ack_parsed.statistics
                                        .accepted_segments
                                    }
                                  </li>
                                  <li>
                                    Rejected:{' '}
                                    {
                                      ackResponse.ack_parsed.statistics
                                        .rejected_segments
                                    }
                                  </li>
                                  <li>
                                    Error Count:{' '}
                                    {
                                      ackResponse.ack_parsed.statistics
                                        .error_count
                                    }
                                  </li>
                                </ul>
                              </div>
                            )}
                            {ackResponse.ack_parsed.functional_group_acks?.map(
                              (ack: FunctionalGroupAck, idx: number) => (
                                <div key={idx} className='mt-2'>
                                  <p>
                                    <strong>Functional Group Status:</strong>
                                    <span
                                      className={`ml-2 font-semibold ${getStatusColor(ack.status)}`}
                                    >
                                      {getStatusText(ack.status)}
                                    </span>
                                  </p>
                                  <p>
                                    <strong>Accepted Count:</strong>{' '}
                                    {ack.accepted_count}
                                  </p>
                                  <p>
                                    <strong>Received Count:</strong>{' '}
                                    {ack.received_count}
                                  </p>
                                </div>
                              )
                            )}
                            {ackResponse.ack_parsed.transaction_set_acks?.map(
                              (tsa: TransactionSetAck, idx: number) => (
                                <div
                                  key={idx}
                                  className='mt-2 pl-4 border-l-2 border-gray-200'
                                >
                                  <p className='font-medium'>
                                    Transaction Set{' '}
                                    {tsa.transaction_set_control_number}:
                                  </p>
                                  <p>
                                    <strong>Status:</strong>
                                    <span
                                      className={`ml-2 font-semibold ${getStatusColor(tsa.status)}`}
                                    >
                                      {getStatusText(tsa.status)}
                                    </span>
                                  </p>
                                  {Boolean(tsa.error_codes?.length) && (
                                    <p className='text-sm text-red-600'>
                                      Error Codes: {tsa.error_codes?.join(', ')}
                                    </p>
                                  )}
                                </div>
                              )
                            )}
                            {Boolean(ackResponse.ack_parsed.errors?.length) && (
                              <div className='mt-2'>
                                <p className='font-semibold text-red-700'>
                                  Errors:
                                </p>
                                <div className='space-y-2 mt-1'>
                                  {ackResponse.ack_parsed.errors?.map(
                                    (error: X12Error, idx: number) => (
                                      <div
                                        key={idx}
                                        className='bg-red-50 p-3 rounded border border-red-200 text-sm'
                                      >
                                        <p className='font-medium text-red-800'>
                                          Error Code {error.error_code}:{' '}
                                          {error.error_description}
                                        </p>
                                        <div className='mt-1 text-red-700'>
                                          <p>
                                            <strong>Segment:</strong>{' '}
                                            {error.segment_id} at position{' '}
                                            {error.segment_position}
                                          </p>
                                          {error.loop_id && (
                                            <p>
                                              <strong>Loop:</strong>{' '}
                                              {error.loop_id}
                                            </p>
                                          )}
                                          {error.context && (
                                            <p>
                                              <strong>Context:</strong>{' '}
                                              {error.context}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className='text-center text-muted-foreground py-8'>
                <p>
                  Fill out the form and click submit to generate X12 837I
                  message
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default X12_837I_Testing;
